import type { AdminRole } from '@sbc/domain-models/admin';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  BookText,
  Bot,
  ChevronRight,
  ClipboardCheck,
  Cpu,
  FlaskConical,
  Gauge,
  Inbox,
  type LucideIcon,
  MessageSquareWarning,
  MessageSquareText,
  MessagesSquare,
  Plug,
  Radio,
  Users,
  Wrench,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@/lib/auth/auth-context';
import { canAccessPath } from '@/lib/auth/roles';
import { cn } from '@/lib/utils';

interface NavLeaf {
  title: string;
  to: string;
}

interface NavItem {
  title: string;
  icon: LucideIcon;
  to?: string;
  items?: NavLeaf[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Grouped by bounded context (TRD: Gateway / Agent team / Agent Admin). Only
// surfaces with a real admin page are listed; new groups attach here as the
// Gateway and Agent contexts gain control-plane screens.
const NAV: NavGroup[] = [
  {
    label: 'Gateway',
    items: [
      {
        title: 'Inbox',
        icon: Inbox,
        items: [{ title: 'Conversations', to: '/inbox' }],
      },
      {
        title: 'Draft review',
        icon: MessageSquareWarning,
        items: [
          { title: 'Review a draft', to: '/review' },
          { title: 'Proposals', to: '/review/proposals' },
        ],
      },
      {
        title: 'Channels',
        icon: Radio,
        items: [{ title: 'Connections', to: '/gateway/connections' }],
      },
      {
        title: 'Agent runs',
        icon: Activity,
        to: '/gateway/runs',
      },
      {
        title: 'Draft shadow',
        icon: Gauge,
        to: '/gateway/draft-shadow',
      },
    ],
  },
  {
    label: 'Agent Admin',
    items: [
      {
        title: 'Agents',
        icon: Bot,
        items: [{ title: 'All agents', to: '/agents' }],
      },
      {
        title: 'Models',
        icon: Cpu,
        to: '/models',
      },
      {
        title: 'Sessions',
        icon: MessagesSquare,
        items: [{ title: 'All sessions', to: '/sessions' }],
      },
      {
        title: 'Outcomes',
        icon: ClipboardCheck,
        to: '/outcomes',
      },
      {
        title: 'Evals',
        icon: FlaskConical,
        items: [
          { title: 'Guide', to: '/evals/guide' },
          { title: 'Suites', to: '/evals/suites' },
          { title: 'Runs', to: '/evals/runs' },
        ],
      },
      {
        title: 'Feedback',
        icon: MessageSquareText,
        to: '/feedback',
      },
      {
        title: 'Users',
        icon: Users,
        items: [{ title: 'Admins', to: '/users' }],
      },
      {
        title: 'MCP',
        icon: Plug,
        items: [{ title: 'Connections', to: '/mcp/connections' }],
      },
      {
        title: 'Tools',
        icon: Wrench,
        to: '/tools',
      },
    ],
  },
  {
    label: 'Knowledge Base',
    items: [
      {
        title: 'Articles',
        icon: BookText,
        items: [
          { title: 'Browse', to: '/knowledge-base' },
          { title: 'All articles', to: '/knowledge-base/all' },
          { title: 'Glossary proposals', to: '/knowledge-base/glossary-proposals' },
        ],
      },
    ],
  },
];

function navItemFor(role: AdminRole, item: NavItem): NavItem | undefined {
  if (item.items) {
    const items = item.items.filter((leaf) => canAccessPath(role, leaf.to));
    return items.length > 0 ? { ...item, items } : undefined;
  }
  return item.to && canAccessPath(role, item.to) ? item : undefined;
}

export function navFor(role: AdminRole): NavGroup[] {
  return NAV.flatMap((group) => {
    const items = group.items.flatMap((item) => navItemFor(role, item) ?? []);
    return items.length > 0 ? [{ ...group, items }] : [];
  });
}

function isLeafActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

// Sibling routes can nest (e.g. /knowledge-base is a prefix of /knowledge-base/all),
// so more than one leaf can match by prefix. The most specific (longest) match wins,
// keeping exactly one leaf highlighted.
function activeLeafTo(pathname: string, leaves: NavLeaf[]): string | undefined {
  return leaves
    .filter((leaf) => isLeafActive(pathname, leaf.to))
    .toSorted((a, b) => b.to.length - a.to.length)[0]?.to;
}

function NavCollapsible({
  item,
  pathname,
}: {
  item: NavItem & { items: NavLeaf[] };
  pathname: string;
}) {
  const { state } = useSidebar();
  const activeTo = activeLeafTo(pathname, item.items);
  const groupActive = activeTo !== undefined;
  const [open, setOpen] = useState(groupActive);
  const firstLeaf = item.items[0];

  // Keep the submenu open when navigation lands on one of its routes (e.g. after
  // the add-admin redirect), even if the user had collapsed it.
  useEffect(() => {
    if (groupActive) {
      setOpen(true);
    }
  }, [groupActive]);

  // Collapsed (icon-only) sidebar can't show a submenu, so the parent icon links
  // straight to its first child instead of toggling.
  if (state === 'collapsed' && firstLeaf) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={groupActive} tooltip={item.title}>
          <Link to={firstLeaf.to}>
            <item.icon />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton isActive={groupActive} onClick={() => setOpen((value) => !value)}>
        <item.icon />
        <span className="flex-1">{item.title}</span>
        <ChevronRight className={cn('transition-transform', open && 'rotate-90')} />
      </SidebarMenuButton>
      {open && (
        <SidebarMenuSub>
          {item.items.map((leaf) => (
            <SidebarMenuSubItem key={leaf.to}>
              <SidebarMenuSubButton asChild isActive={leaf.to === activeTo}>
                <Link to={leaf.to}>{leaf.title}</Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

function isItemActive(pathname: string, item: NavItem): boolean {
  if (item.items) {
    return item.items.some((leaf) => isLeafActive(pathname, leaf.to));
  }
  return item.to ? isLeafActive(pathname, item.to) : false;
}

function NavGroupSection({ group, pathname }: { group: NavGroup; pathname: string }) {
  const { state } = useSidebar();
  const groupActive = group.items.some((item) => isItemActive(pathname, item));
  const [open, setOpen] = useState(true);

  // Reveal the section whenever navigation lands inside it, even if collapsed.
  useEffect(() => {
    if (groupActive) {
      setOpen(true);
    }
  }, [groupActive]);

  // The icon-only sidebar hides group labels, so there's nothing to toggle — keep
  // every section's items visible in that mode.
  const showItems = open || state === 'collapsed';

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="p-0">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center px-2 py-1 hover:text-[var(--color-sidebar-foreground)]"
        >
          <span className="flex-1 text-left">{group.label}</span>
          <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        </button>
      </SidebarGroupLabel>
      {showItems && (
        <SidebarMenu>
          {group.items.map((item) =>
            item.items ? (
              <NavCollapsible
                key={item.title}
                item={{ ...item, items: item.items }}
                pathname={pathname}
              />
            ) : (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={item.to ? isLeafActive(pathname, item.to) : false}
                  tooltip={item.title}
                >
                  <Link to={item.to}>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ),
          )}
        </SidebarMenu>
      )}
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user } = useAuth();
  const nav = user ? navFor(user.role) : [];

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-1 py-1.5">
          <img src="/favicon.svg" alt="" className="size-8 shrink-0 rounded-md" />
          <span className="font-semibold group-data-[state=collapsed]:hidden">Agent Platform</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {nav.map((group) => (
          <NavGroupSection key={group.label} group={group} pathname={pathname} />
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
